/**
 * All public marketing copy, in one file.
 *
 * ⚠️ DRAFT — open item #7. Peter writes this or approves it. It's isolated here
 * so he can mark up one file rather than hunting through components, and so a
 * copy change never risks a layout change.
 *
 * Tone target (spec §3): "humorous rather than banners." Dry, self-deprecating,
 * tabloid-football. Not a SaaS landing page.
 *
 * ⚠️ TWO OPEN DECISIONS baked into this file as the conservative option:
 *
 * 1. NO PRIZE-POOL FIGURES IN THIS FILE. Spec §5 forbids prize money on public
 *    surfaces: amounts move with entrant count and aren't final until entries
 *    close. This file is compiled into the public bundle, so every payout figure
 *    lives in Firestore `config/prizes` and reaches /prizes through the
 *    getPrizeStructure callable — which withholds the amounts entirely until an
 *    admin sets `publishAmounts`. `npm run verify:bundle` scans this file.
 *    The entry fee IS included: it's a fixed, knowable cost that people need
 *    before they fill in a form, not a projection that could turn out wrong.
 *
 * 2. NO NAMED PLAYERS. Peter named the notable entrants in a private email.
 *    Publishing real people's names on a public page is a wider audience than
 *    he chose. Achievements are listed unnamed until he confirms.
 */

/**
 * Facts repeated across several screens. One definition each — the entry fee
 * appears on the scoreboard, in how-it-works and in the Join sidebar, and three
 * copies is three chances to update two of them.
 */
export const site = {
  entryFee: '$300',
  deadlineDate: 'Friday 21 August',
  deadlineTime: '18:30 UK',
  email: 'theref@refsabanker.com',
} as const;

export const nav = {
  /** Join is appended separately — it renders as the CTA pill, not a link. */
  links: [
    { to: '/history', label: 'History' },
    { to: '/league', label: 'League' },
    { to: '/prizes', label: 'Prizes' },
  ],
  footerBlurb:
    "The World's Most Serious Fun League. Est. 2017. Invite only.",
  fineprint:
    'Not affiliated with the Premier League or Fantasy Premier League · Play for fun, not for a living',
} as const;

/**
 * The closing band at the foot of every screen except the landing page.
 *
 * Three versions, because "Still interested?" is the wrong thing to say to two
 * of the three people who reach the bottom of /prizes:
 *
 *   default     nobody knows who you are — ask.
 *   registered  you have already submitted the interest form; that is how you
 *               are seeing the figures at all. Asking again would be absurd, so
 *               tell them what happens next instead.
 *   member      you hold the league password, so you are already in. Point you
 *               at the live tables rather than at a form.
 */
export const ctaBand = {
  heading: 'Still interested?',
  headingHistory: 'Fancy your name on it?',
  /**
   * No date in here, deliberately.
   *
   * This band renders on the landing page and /history, both of which are
   * Firebase-free by design and so cannot know whether entries are still open.
   * A hardcoded "closes Friday 21 August" was true for about six weeks and then
   * wrong for the rest of the year — the site spent the evening of the 2026/27
   * opener advertising a deadline that had already gone.
   *
   * What is written here is the standing rule, which is true in any month. The
   * page that knows the answer is /join, and it says so there.
   */
  body: 'Entries close at the Gameweek 1 deadline. No late entries, no exceptions, no matter who invited you.',
  /**
   * One line of character under the functional body copy. Deliberately not a
   * second sentence of information — the band already says the only thing
   * anyone needs to act on, and this is the reason to want to.
   *
   * Only on the default band: the registered and member variants are talking to
   * people who are already in, and a recruitment refrain reads as a form letter
   * to them.
   */
  flourish: 'Ten seasons. No advertising. No nonsense.',
  label: 'Register your interest',
  sub: 'Five fields, one screen',

  /** `flourish: null` on both — see the note on it above. */
  registered: {
    heading: 'You are on the list.',
    flourish: null,
    body: 'The referee has your details and will come back with payment. Nothing is confirmed until the money clears.',
    label: 'Read the rules',
    sub: 'And the key dates',
    to: '/league',
  },

  member: {
    heading: 'You are already in.',
    flourish: null,
    body: 'Nothing to register — the live tables and who currently holds what are on the League page.',
    label: 'Open the league',
    sub: 'Standings and awards',
    to: '/league',
  },
} as const;

export const landing = {
  /**
   * Rendered as two display lines. The wordmark is the hero.
   *
   * "Ref’s", not "Referee’s" — Peter's call, and it matches what everyone in the
   * WhatsApp group already calls it. The wordmark in Layout.tsx and the title
   * and og: tags in index.html all say the same thing; the note in robots.txt
   * covers what the rename costs in search.
   */
  wordmark: ['Ref’s a', 'Banker'],
  tagline: "The World's Most Serious Fun League",
  /** Rule-separated strip under the tagline. */
  credentials: ['Est. 2017', 'Invite only', 'Global'],
  ctaPrimary: 'Register your interest',
  ctaSub: 'Five fields, one screen',

  /**
   * The split-flap scoreboard.
   *
   * Deliberately dollar-free apart from the entry fee. "35% won something" does
   * the same persuasive work as a prize-pool figure without publishing a number
   * that (a) isn't final until entries close and (b) you may not want indexed by
   * Google. See the note at the top of this file.
   *
   * The count is 254, and it is this season's rather than last season's.
   *
   * It used to read "188 entrants last season", which was the defensible number
   * while 26/27 was still recruiting — Peter's email said the league was "aiming
   * to smash 200 managers for the first time", so anything above 200 was an
   * overclaim. It smashed them: 254 entered, and the same arithmetic still
   * holds — the entry count times the fee is exactly the published payout
   * total, so the pot remains a pure pass-through of entries. (The figures
   * themselves stay out of this file: verify:bundle failed the build when this
   * very comment quoted them, which is the guard working as designed. They are
   * reconciled in scripts/seed-config.mjs, which is not shipped.)
   *
   * The label moved to "this season" deliberately. Entries are closed and the
   * number is final, so it is now a fact rather than a projection, and it is the
   * larger and more useful one. It will want revisiting when 27/28 opens.
   */
  scoreboard: {
    label: 'The scoreboard',
    /** Season, not a deadline — this line outlived the last one by ten months. */
    meta: '2026/27 · Tenth season',
    stats: [
      { value: '10th', label: 'season' },
      { value: '254', label: 'entrants this season' },
      { value: '23', label: 'places paid' },
      { value: '36%', label: 'won something' },
      { value: site.entryFee, label: 'entry for the season' },
    ],
  },

  pitch: {
    heading: 'Where strategy meets banter. All season.',
    body: 'Ten months of misery for a modest return. Tenth season, still invite only, still no advertising, still worth every penny.',
  },

  /**
   * ⚠️ Names withheld pending consent — see the note at the top of this file.
   * Peter listed the individuals by name in a private email to the group;
   * publishing them on an indexable page is a broader audience than that.
   * The unnamed version is also funnier, so this is not much of a sacrifice.
   */
  field: {
    heading: 'Who you are up against',
    items: [
      'A former FPL World Champion',
      'A former FPL world runner-up',
      'A former Premier League player',
      'At least one billionaire',
      'A great many people who are just guessing',
    ],
  },

  /**
   * The money teaser, kept as structure and stripped of amounts. The four
   * categories carry the argument on their own; the figures live on /prizes,
   * where the server decides whether they are publishable yet.
   */
  manyWays: {
    eyebrow: 'What you are playing for',
    heading: 'You do not have to win to win',
    body: 'Finishing top of a field this size is, statistically, not your problem. Prizes are spread deliberately thin — twenty-three places pay out, and last season more than a third of the league won something. You need one good week, not ten good months. Every entry fee goes back into the pot; the ref takes nothing.',
    cta: 'See the full breakdown',
    categories: [
      { title: 'Overall table', detail: 'Twenty-three places paid' },
      { title: 'Every gameweek', detail: 'Highest score wins' },
      { title: 'Every month', detail: 'First and second' },
      { title: 'The specialists', detail: 'Eight separate awards' },
    ],
  },

  highRollers: {
    heading: 'High Rollers',
    body: 'A second, smaller league for the brave. Separate entry on top of the main league — you must be in the main league to qualify. Ask the referee.',
  },

  howItWorks: {
    heading: 'How it works',
    steps: [
      {
        title: 'Register your interest',
        detail: 'Five fields, one screen. Tell him who you are and who invited you.',
      },
      {
        title: `Pay the ${site.entryFee}`,
        detail:
          'One payment, one season. Nothing is taken out for admin, hosting or the ref’s dinner.',
      },
      {
        title: 'Join the league',
        detail:
          'You get the FPL league code and the WhatsApp group. The group is not optional.',
      },
      {
        title: 'Suffer politely',
        detail:
          'Thirty-eight gameweeks. Ten monthly races. One cup. No refunds for bad captaincy.',
      },
    ],
  },
} as const;

export const join = {
  eyebrow: 'Entry is by invitation',
  heading: 'Register your interest. Tell him who sent you.',
  lede: 'This is not registration — it tells the referee you want in. He comes back to you with payment details.',
  formLabel: 'Register your interest',
  existingPlayerNote:
    'Already played before? You are in the registry and the WhatsApp group. You do not need this form.',
  successHeading: "That's logged.",
  successBody: 'The ref will be in touch.',

  /**
   * The form's own labels and placeholders. Here rather than inline in Join.tsx
   * so this file really is the only one Peter has to mark up.
   */
  form: {
    name: { label: 'Your name', placeholder: 'As it goes on the table' },
    email: { label: 'Email', placeholder: 'you@somewhere.com' },
    phone: {
      label: 'Phone',
      optional: '— optional',
      /** For the group, not for a phone call. Nobody is ringing anyone. */
      placeholder: 'For the WhatsApp group',
    },
    referredBy: {
      label: 'Who invited you',
      placeholder: 'Name, or the group they run',
    },
    leagues: { label: 'Which leagues' },
    message: {
      label: 'Anything else',
      optional: '— optional',
      placeholder: 'Keep it shorter than your team name.',
    },
    /**
     * The button promises money, so the success screen has to deliver it —
     * `join.successCta` points straight at /prizes, and submitting this form is
     * what puts your email on the registered tier there. If that link ever goes,
     * this label becomes a bait.
     */
    submit: 'Show me the money',
    submitting: 'Sending…',
    /**
     * Sits under the button, where the "what happens to my address" question
     * actually gets asked. The aside below it already says we use the address
     * for the league and nothing else; this says what we send.
     */
    reassurance: 'No spam. Just The Ref’s weekly updates during the season.',
  },

  next: {
    heading: 'What happens next',
    count: 'Four steps',
    steps: [
      { title: 'The ref replies', detail: 'Usually within a week. He checks the invite is real.' },
      {
        title: `You pay ${site.entryFee}`,
        detail: `Bank transfer, one payment, before the Gameweek 1 deadline on ${site.deadlineDate}.`,
      },
      {
        title: 'You get the code',
        detail: 'The FPL classic-league code and the WhatsApp invite.',
      },
      {
        title: 'You pick a team',
        detail: 'And a team name you can live with for ten months.',
      },
    ],
  },

  fee: {
    label: 'Entry for the season',
    amount: site.entryFee,
    note: 'All of it goes into the pot. Nothing is taken out for admin, hosting, or the ref’s dinner.',
  },

  closes: {
    label: 'Entries close',
    when: `Gameweek 1 · ${site.deadlineDate}`,
    note: 'No late entries, no exceptions, no matter who invited you.',
  },

  /**
   * /join after the Gameweek 1 deadline.
   *
   * The form stays up and keeps working — it is the only thing on the site that
   * captures anyone, and someone who finds the league in October is a real
   * entrant for next season. What changes is what it claims to be. Telling a
   * late arrival that "the ref will be in touch with payment details" when the
   * season is eight gameweeks old is how you end up owing someone an apology.
   *
   * Peter can override the timing either way from /admin; the copy follows the
   * server's answer, and the success screen follows the server's answer about
   * the submission that was actually filed. Neither is decided in this file.
   */
  closed: {
    eyebrow: 'Entries for this season have closed',
    heading: 'You have missed this one. Get your name down for the next.',
    lede: 'The Gameweek 1 deadline has gone and the pot is fixed, so that is the table set for this year. Leave your details and the referee reads this list first when invitations go out.',
    formLabel: 'Join the waiting list',

    successHeading: "You're on the list.",
    successBody: 'Nothing to pay and nothing to do. The ref has you down for next season and will write long before the deadline.',

    next: {
      heading: 'What happens next',
      count: 'Two steps',
      steps: [
        {
          title: 'Nothing, for now',
          detail: 'No payment, no team, no deadline. The season has started without you.',
        },
        {
          title: 'The ref writes in July',
          detail: 'Invitations go out before the new season, and this list is read before anyone else is asked.',
        },
      ],
    },

    closes: {
      label: 'Entries reopen',
      when: 'Next July',
      note: 'One round a year. The waiting list goes first, then the invitations.',
    },
  },

  faq: {
    heading: 'Questions the ref gets every August',
    items: [
      {
        q: 'Can I enter two teams?',
        a: 'No. One entry per person, and the ref does check.',
      },
      {
        q: 'Can I join after Gameweek 1?',
        a: 'No. The pot is fixed at the deadline so the numbers can be published.',
      },
      {
        q: 'Do I need an invite?',
        a: 'Yes. Write anyway — if someone vouches for you, that counts.',
      },
      {
        q: 'How are prizes paid?',
        a: 'Bank transfer, once the final gameweek is confirmed. The ref does not commit to a date and is usually quicker than you expect.',
      },
      {
        q: 'Who counts as a newcomer?',
        a: 'Genuinely new to Fantasy Premier League, not just new to this league. The ref decides, and he does ask.',
      },
      {
        q: 'What if I stop playing?',
        a: 'Nothing happens. No refunds, and your abandoned team still counts.',
      },
      {
        q: 'Is the ref a player?',
        a: 'Yes. He has never won it and takes this as proof of impartiality.',
      },
    ],
  },
} as const;

export const history = {
  eyebrow: 'Since 2017',
  heading: 'Nine seasons. Eight champions who still bring it up.',
  lede: 'It started as a group of thirty-seven and a spreadsheet. It is now a group of two hundred and fifty-four, a slightly better spreadsheet, and — apparently — a website.',
  rollHeading: 'Roll of honour',
  /**
   * Historical prize pools ARE allowed on this page — invariant #3 covers the
   * current season only, and "paid out to date" is the recruitment pitch. The
   * figure is summed from the `seasons` docs at render time rather than written
   * here, so it can never be a stale hardcoded number.
   */
  paidOutLabel: 'Paid out to date',
  records: {
    heading: 'Records that will not be beaten. By you, anyway.',
    /**
     * The archive has landed, so this is a fallback rather than the live state:
     * the records section renders from `seasons` and only falls back to this if
     * the fetch comes back empty.
     */
    empty:
      'The record book comes out of the same workbook as the roll of honour. It lands when that does.',
  },
} as const;

export const league = {
  eyebrow: 'How the league works',
  heading: 'One season. Four competitions. No excuses.',
  lede: 'Standard Fantasy Premier League scoring, one team per person, thirty-eight gameweeks. Everything else is our own.',

  /**
   * Prize amounts deliberately absent. The design mock spelled the top and
   * bottom overall payouts out inline here; a figure in this file is a figure in
   * the public bundle, which is the §5 violation verify:bundle exists to catch —
   * and it caught exactly this comment when it quoted them. The amounts live on
   * /prizes, served per request by getPrizeStructure.
   */
  competitions: [
    {
      title: 'The overall table',
      detail:
        'Total points across all 38 gameweeks. Twenty-three places paid, on a sliding scale.',
    },
    {
      title: 'Weekly & monthly',
      detail:
        'A prize for the highest score in every gameweek, and for the top two in each of the ten months.',
    },
    {
      title: 'The cup',
      detail:
        'Straight knockout from Gameweek 20. Head to head, higher score goes through, ties broken by overall rank.',
    },
    {
      title: 'The awards',
      detail:
        'Best newcomer, season high score, lowest winning score, highest team value and best team name.',
    },
  ],

  /**
   * Peter's edits, August 2026: the team-name rule and the 14-day payment
   * promise both came out. The payment one is deliberate — he pays sooner than
   * that in practice and does not want a date he is held to. Don't reinstate it
   * anywhere; the FAQ and the key-dates list were changed to match.
   *
   * The newcomer rule went in for the opposite reason: it is the question he
   * fields most often, so it is now written down in the place people are sent
   * to look.
   */
  rules: {
    heading: 'The rules',
    count: 'Nine of them',
    items: [
      'One entry per person. One person per entry. The ref checks.',
      'Entry fee must clear before the Gameweek 1 deadline. No late entries.',
      'Standard FPL scoring and standard FPL chips. We add nothing and take nothing away.',
      'Weekly and monthly prizes are decided on gameweek points after deductions.',
      'Best newcomer means genuinely new to Fantasy Premier League, not just new to this league. The ref decides.',
      'Ties are split equally. Nobody has ever been happy about this.',
      'Cup ties are broken by overall league rank at that gameweek.',
      'Abandoned or void gameweeks follow whatever FPL decides. We do not relitigate it.',
      'The ref’s decision is final. The ref is also a player. Yes, we know.',
    ],
  },

  dates: {
    heading: 'Key dates',
    count: '2026/27',
    items: [
      { label: 'Invitations sent', value: 'Late July' },
      {
        label: 'Entries close — GW1 deadline',
        value: site.deadlineTime,
        lead: true,
      },
      { label: 'Final pot confirmed', value: 'GW1 + 24h' },
      { label: 'Cup round one', value: 'Approx. GW20' },
      { label: 'Cup final', value: 'Gameweek 37' },
      { label: 'Season ends', value: 'Gameweek 38' },
      /* Not "within 14 days" — see the note on `rules`. */
      { label: 'Prizes paid', value: 'Once GW38 is final' },
    ],
  },

  months: {
    label: 'The monthly races',
    /**
     * Ten, one per calendar month of the season.
     *
     * The design mock said nine, with April and May combined. The 2025/26
     * workbook says otherwise — it lists ten separate monthly winners, and the
     * prize aggregate agrees ($5,000 monthly 1st is 10 x $500, not 9). Nine
     * understated the pot by exactly $800, which is the discrepancy that had
     * been sitting unexplained against 188 x $300.
     *
     * The award engine never had this wrong: it groups by Europe/London
     * calendar month and merges nothing, so it already produced ten.
     */
    items: ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May'],
    note: 'Ten races, one per calendar month of the season. Cup rounds are set once the field size is known.',
  },

  /**
   * Members only. The footer points here for "the WhatsApp group" — the invite
   * itself is served by getStandings once the password is in, never from the
   * bundle.
   */
  group: {
    label: 'The WhatsApp group',
    note: 'Where the results, the abuse and the password live. If you have lost the invite or changed phone, this is the way back in.',
    cta: 'Open the invite',
  },

  tableHeading: 'The table, as it stands',
  passwordPrompt: 'Password (it is in the WhatsApp group)',
  mainTable: 'Main league',
  highRollersTable: 'High Rollers',
  /**
   * Shown instead of a table before gameweek 1. Names the 09:00 rule explicitly
   * because the alternative is Peter fielding the same WhatsApp message from
   * forty people on a Sunday night.
   */
  noResults:
    'Standings appear once gameweek 1 has been played and scores are final. FPL doesn’t lock scores until 09:00 UK the morning after a gameweek’s last match, so give it until then.',
} as const;

/**
 * /prizes — structure only. Every label here is safe in the bundle; not one
 * amount is. The figures arrive from getPrizeStructure keyed by `id`, and the
 * page renders labels alone when the server withholds them.
 */
export const prizes = {
  eyebrow: 'Potential payouts',
  heading: 'Something to play for every week of the season.',
  lede: 'Four ways to get paid: every week, every month, the overall table, and a set of awards for the specialists.',
  /**
   * Not decorative. Keep it until entries close — it is the only thing on the
   * page saying the figures aren't final. Phrased as growth rather than as a
   * caveat, which is the same disclosure read the right way up.
   */
  provisional: 'Numbers grow with every new entrant',

  /**
   * The unlock. Public visitors see the structure with the figures blurred;
   * leaving an email or entering the league password reveals them.
   *
   * The blur is cosmetic — the amounts are simply not in the public response —
   * so this copy must not imply the numbers are sitting there behind a filter.
   */
  /**
   * The unlock. There is exactly one place on this site to hand over details —
   * the interest form — and this panel points at it. The email box here is a
   * lookup against a form you have already filled in, never a second capture.
   */
  unlock: {
    label: 'The figures are behind the ref',
    body: 'Register your interest and the payouts appear. It is five fields and one screen, and it is the same form the referee works from — there is nowhere else to sign up.',
    registerCta: 'Register your interest',
    registerSub: 'Five fields, one screen',

    passwordToggle: 'I have the league password',
    backToggle: 'Back',

    passwordLabel: 'League password',
    passwordHint: 'It is in the WhatsApp group.',
    passwordCta: 'Unlock',

    doneLabel: 'Unlocked',
    /**
     * The reveal is the second conversion moment: someone who filled the form in
     * is now looking at what it pays, which is the best chance in the whole
     * funnel to move them along. The caveat that used to sit here is still
     * owed — it moved to `unlockedNote`, so it reads as fine print rather than
     * as the first thing said.
     *
     * The ask is deliberately "chase him", not "ask whether there is a spot".
     * This tier is only reachable by having submitted the form, so anyone
     * reading it is already on the list — and the closing band on this same page
     * tells them so. The real gap for these people is registered-but-unpaid,
     * which is the one Peter chases by hand, so that is what the ask points at.
     */
    unlockedRegistered:
      'Now you know what is at stake. The ref has your details — if he has not come back to you yet, chase him.',
    unlockedPassword:
      'Unlocked with the league password. The live tables are on the League page.',
    /**
     * The "not final" disclosure, kept on both unlocked tiers. The figures move
     * with the entrant count and someone reading them off this page needs to
     * know that, whichever door they came through.
     */
    unlockedNote:
      'The figures move with the entrant count and are not final until entries close.',
    error: "That didn't work. Try again, or email theref@refsabanker.com.",
    /** Shown when an admin has pulled the figures for everyone. */
    notReady:
      'The referee is recalculating the pot, so the figures are off the site for the moment. The structure below is fixed — only the numbers move.',
  },

  pitchLabel: 'The pot, by position',
  formation: '3–6–3',

  legend: [
    { key: 'podium', label: 'The podium' },
    { key: 'award', label: 'Other awards' },
    { key: 'periodic', label: 'Weekly & monthly' },
    { key: 'newcomer', label: 'Best newcomer' },
  ],

  /**
   * The 3–6–3. `x`/`y` are percentages of the pitch panel for the landscape
   * layout; `nx`/`ny` for the portrait one below 760px. Position is presentation,
   * so it lives here rather than in config — only `id` crosses to the server.
   */
  chips: [
    { id: 'newcomer', label: 'Best newcomer', short: 'Best newcomer', tone: 'newcomer', x: 6.5, y: 50, nx: 50, ny: 10 },
    { id: 'weekly1', label: 'Weekly 1st', short: 'Weekly 1st', tone: 'periodic', x: 22, y: 22, nx: 26, ny: 30 },
    { id: 'monthly1', label: 'Monthly 1st', short: 'Month 1st', tone: 'periodic', x: 22, y: 50, nx: 50, ny: 30 },
    { id: 'monthly2', label: 'Monthly 2nd', short: 'Month 2nd', tone: 'periodic', x: 22, y: 78, nx: 74, ny: 30 },
    { id: 'highScore', label: 'Highest winning gameweek', short: 'Highest GW', tone: 'award', x: 46, y: 13, nx: 27, ny: 50 },
    { id: 'lowestWinning', label: 'Lowest winning gameweek', short: 'Lowest GW', tone: 'award', x: 46, y: 27.4, nx: 50, ny: 50 },
    { id: 'cupWinner', label: 'Cup winner', short: 'Cup 1st', tone: 'award', x: 46, y: 41.8, nx: 73, ny: 50 },
    { id: 'cupRunnerUp', label: 'Cup runner-up', short: 'Cup 2nd', tone: 'award', x: 46, y: 58.2, nx: 27, ny: 70 },
    { id: 'teamValue', label: 'Highest team value', short: 'Team value', tone: 'award', x: 46, y: 72.6, nx: 50, ny: 70 },
    { id: 'bestName', label: 'Best team name', short: 'Best name', tone: 'award', x: 46, y: 87, nx: 73, ny: 70 },
    { id: 'overall1', label: 'Overall 1st', short: 'Overall 1st', tone: 'podium', x: 75, y: 22, nx: 27, ny: 90 },
    { id: 'overall2', label: 'Overall 2nd', short: 'Overall 2nd', tone: 'podium', x: 75, y: 50, nx: 50, ny: 90 },
    { id: 'overall3', label: 'Overall 3rd', short: 'Overall 3rd', tone: 'podium', x: 75, y: 78, nx: 73, ny: 90 },
  ],

  overall: {
    heading: 'Overall table',
    count: '23 places paid',
    podiumLabel: 'Podium',
    /** ids 1–23; the podium rule is drawn after the third. */
    places: [
      'overall1', 'overall2', 'overall3', 'overall4', 'overall5', 'overall6',
      'overall7', 'overall8', 'overall9', 'overall10', 'overall11', 'overall12',
      'overall13', 'overall14', 'overall15', 'overall16', 'overall17', 'overall18',
      'overall19', 'overall20', 'overall21', 'overall22', 'overall23',
    ],
  },

  weekly: {
    heading: 'Weekly',
    count: '38 gameweeks',
    rows: [{ id: 'weekly1', label: 'Weekly 1st — per gameweek', lead: true }],
  },

  monthly: {
    heading: 'Monthly',
    count: '10 months',
    rows: [
      { id: 'monthly1', label: 'Monthly 1st', lead: true },
      { id: 'monthly2', label: 'Monthly 2nd', lead: false },
    ],
  },

  awards: {
    heading: 'All other awards',
    count: '8 awards',
    rows: [
      { id: 'newcomer', label: 'Best newcomer', lead: true },
      { id: 'highScore', label: 'Highest winning gameweek', lead: false },
      { id: 'cupWinner', label: 'Cup winner', lead: false },
      { id: 'cupRunnerUp', label: 'Cup runner-up', lead: false },
      { id: 'lowestWinning', label: 'Lowest winning gameweek', lead: false },
      { id: 'teamValue', label: 'Highest team value', lead: false },
      { id: 'bestName', label: 'Best team name', lead: false },
      /*
       * Best recruiter carries no figure — Peter has not set one, and the row
       * in config/prizes is blank on purpose. Unlocked, it renders an em dash
       * rather than a blurred placeholder, so it reads as undecided rather than
       * as a figure being kept from you.
       */
      { id: 'bestRecruiter', label: 'Best recruiter', lead: false },
    ],
  },

  /**
   * High Rollers — the optional second league, played on top of the main one.
   *
   * Labels only, like everything else here. The figures arrive from
   * config/prizes.highRollers through the same gate as the main payouts, so a
   * public visitor sees this structure with the amounts blurred.
   */
  highRollers: {
    heading: 'High Rollers',
    count: 'Separate entry',
    body: 'A smaller, steeper league for the brave. You must be in the main league to enter, and the pot is its own — nothing here comes out of the main prize money.',
    rows: [
      { id: 'hr1', label: '1st', lead: true },
      { id: 'hr2', label: '2nd', lead: false },
      { id: 'hr3', label: '3rd', lead: false },
      { id: 'hr4', label: '4th', lead: false },
      { id: 'hr5', label: '5th', lead: false },
      { id: 'hr6', label: '6th', lead: false },
      { id: 'hr7', label: '7th', lead: false },
      { id: 'hr8', label: '8th', lead: false },
      { id: 'hr9', label: '9th', lead: false },
      { id: 'hrHighestGw', label: 'Highest gameweek (10th–32nd)', lead: false },
    ],
    totalLabel: 'High Rollers pot',
  },

  total: {
    label: 'Indicative total',
    note: 'Every entry fee goes back into the pot — the ref takes nothing. Figures move with the number of entrants and are confirmed after the Gameweek 1 deadline.',
  },
} as const;
